import { fetchJSON } from './api';

const STORAGE_KEY = 'dse.frontpage.debots.v1';
const UPDATE_EVENT = 'dse:frontpage:debots:updated';
const FRONTPAGE_TOGGLE_UPDATE_EVENT = 'dse:frontpage:three-d-terminal:updated';
const GALLERY_DEFAULTS_STORAGE_KEY = 'poiesis.galleryItemDefaults.v1';
const FRONTPAGE_TOGGLE_SYNC_THROTTLE_MS = 2500;

let remoteThreeDAndTerminalEnabled = null;
let remoteThreeDAndTerminalSyncInFlight = null;
let remoteThreeDAndTerminalLastSyncTs = 0;

const DEFAULT_BOTS = [
  {
    id: 'debot-nexus',
    name: 'NEXUS',
    description: 'Main bot for gallery orientation and quick answers.',
    online: true,
    faqs: [
      {
        id: 'nexus-faq-1',
        question: 'How do I switch gallery?',
        answer: 'Click a gallery title in overview, or use the arrow controls in 3D overview.'
      },
      {
        id: 'nexus-faq-2',
        question: 'What is 3D-explore?',
        answer: '3D-explore transitions from 2D overview into an interactive 3D gallery overview.'
      },
      {
        id: 'nexus-faq-3',
        question: 'How do I go back to 2D?',
        answer: 'Open the 3D terminal and press the 2D button for a fast shutdown transition.'
      }
    ]
  },
  {
    id: 'debot-archivist',
    name: 'ARCHIVIST',
    description: 'Bot for metadata and context references.',
    online: true,
    faqs: [
      {
        id: 'arch-faq-1',
        question: 'Which bot is answering now?',
        answer: 'The selected bot in the dropdown answers. You can switch active bot anytime.'
      },
      {
        id: 'arch-faq-2',
        question: 'Can I turn a bot off?',
        answer: 'Yes. Use ONLINE/OFFLINE to enable or disable the current bot.'
      }
    ]
  }
];

function inBrowser() {
  return typeof window !== 'undefined';
}

function getCookieValue(name) {
  if (!inBrowser()) return '';
  const encodedName = `${encodeURIComponent(name)}=`;
  const parts = String(document.cookie || '').split('; ');
  const matched = parts.find((part) => part.startsWith(encodedName));
  if (!matched) return '';
  try {
    return decodeURIComponent(matched.slice(encodedName.length));
  } catch {
    return '';
  }
}

function setCookieValue(name, value) {
  if (!inBrowser()) return;
  const encodedName = encodeURIComponent(name);
  const encodedValue = encodeURIComponent(String(value || ''));
  document.cookie = `${encodedName}=${encodedValue}; path=/; max-age=31536000; samesite=lax`;
}

function getGalleryDefaultsRaw() {
  if (!inBrowser()) return '';

  const fromCookie = getCookieValue(GALLERY_DEFAULTS_STORAGE_KEY);
  if (fromCookie) return fromCookie;

  try {
    return window.localStorage.getItem(GALLERY_DEFAULTS_STORAGE_KEY) || '';
  } catch {
    return '';
  }
}

function persistGalleryDefaultsThreeDAndTerminalEnabled(nextEnabled) {
  if (!inBrowser()) return;

  const fallbackRaw = getGalleryDefaultsRaw();
  let parsed = {};
  if (fallbackRaw) {
    try {
      const candidate = JSON.parse(fallbackRaw);
      if (candidate && typeof candidate === 'object' && !Array.isArray(candidate)) {
        parsed = candidate;
      }
    } catch {
      parsed = {};
    }
  }

  parsed.threeDAndTerminalEnabled = nextEnabled !== false;
  try {
    const serialized = JSON.stringify(parsed);
    window.localStorage.setItem(GALLERY_DEFAULTS_STORAGE_KEY, serialized);
    setCookieValue(GALLERY_DEFAULTS_STORAGE_KEY, serialized);
  } catch {
    // Ignore local caching failures.
  }
}

async function syncFrontpageThreeDAndTerminalEnabledFromApi(force = false) {
  if (!inBrowser()) return null;
  if (remoteThreeDAndTerminalSyncInFlight) return remoteThreeDAndTerminalSyncInFlight;

  const now = Date.now();
  if (
    !force
    && remoteThreeDAndTerminalLastSyncTs > 0
    && (now - remoteThreeDAndTerminalLastSyncTs) < FRONTPAGE_TOGGLE_SYNC_THROTTLE_MS
  ) {
    return remoteThreeDAndTerminalEnabled;
  }

  remoteThreeDAndTerminalSyncInFlight = (async () => {
    try {
      const payload = await fetchJSON('/api/forside', { cache: 'no-store' });
      const nextEnabled = payload?.threeDAndTerminalEnabled !== false;
      const didChange = remoteThreeDAndTerminalEnabled !== nextEnabled;
      remoteThreeDAndTerminalEnabled = nextEnabled;
      remoteThreeDAndTerminalLastSyncTs = Date.now();

      persistGalleryDefaultsThreeDAndTerminalEnabled(nextEnabled);
      if (didChange) {
        emitFrontpageToggleUpdate();
      }

      return remoteThreeDAndTerminalEnabled;
    } finally {
      remoteThreeDAndTerminalSyncInFlight = null;
    }
  })();

  return remoteThreeDAndTerminalSyncInFlight;
}

function fallbackFaq(input = {}, index = 0) {
  const fallbackId = `faq-${index + 1}`;
  return {
    id: String(input.id || fallbackId),
    question: String(input.question || '').trim(),
    answer: String(input.answer || '').trim(),
  };
}

function normalizeBot(input = {}, index = 0) {
  const fallbackId = `bot-${index + 1}`;
  const faqs = Array.isArray(input.faqs)
    ? input.faqs
      .map((faq, faqIndex) => fallbackFaq(faq, faqIndex))
      .filter((faq) => faq.question || faq.answer)
    : [];

  return {
    id: String(input.id || fallbackId),
    name: String(input.name || `Bot ${index + 1}`).trim(),
    description: String(input.description || '').trim(),
    online: input.online !== false,
    faqs,
  };
}

function emitUpdate() {
  if (!inBrowser()) return;
  try {
    window.dispatchEvent(new CustomEvent(UPDATE_EVENT));
  } catch {
    // Ignore event dispatch failures.
  }
}

function emitFrontpageToggleUpdate() {
  if (!inBrowser()) return;
  try {
    window.dispatchEvent(new CustomEvent(FRONTPAGE_TOGGLE_UPDATE_EVENT));
  } catch {
    // Ignore event dispatch failures.
  }
}

export function getDebotConfigs() {
  if (!inBrowser()) return DEFAULT_BOTS;

  try {
    const raw = window.localStorage.getItem(STORAGE_KEY);
    if (!raw) return DEFAULT_BOTS;

    const parsed = JSON.parse(raw);
    if (!Array.isArray(parsed) || !parsed.length) return DEFAULT_BOTS;

    const normalized = parsed.map((entry, index) => normalizeBot(entry, index));
    return normalized.length ? normalized : DEFAULT_BOTS;
  } catch {
    return DEFAULT_BOTS;
  }
}

export function getDefaulterTerminalFaqs() {
  if (!inBrowser()) return [];

  try {
    const raw = getGalleryDefaultsRaw();
    if (!raw) return [];
    const parsed = JSON.parse(raw);
    const faqs = Array.isArray(parsed?.terminalFaqs) ? parsed.terminalFaqs : [];
    return faqs
      .map((faq, index) => fallbackFaq(faq, index))
      .filter((faq) => faq.question);
  } catch {
    return [];
  }
}

export function getFrontpageThreeDAndTerminalEnabled() {
  if (!inBrowser()) return true;

  if (typeof remoteThreeDAndTerminalEnabled === 'boolean') {
    return remoteThreeDAndTerminalEnabled;
  }

  try {
    const raw = getGalleryDefaultsRaw();
    if (!raw) return true;
    const parsed = JSON.parse(raw);
    return parsed?.threeDAndTerminalEnabled !== false;
  } catch {
    return true;
  }
}

export function subscribeFrontpageThreeDAndTerminalEnabled(onChange) {
  if (!inBrowser()) return () => {};

  let lastDefaultsRaw = getGalleryDefaultsRaw();
  let lastValue = getFrontpageThreeDAndTerminalEnabled();

  const notifyIfChanged = () => {
    const nextDefaultsRaw = getGalleryDefaultsRaw();
    if (nextDefaultsRaw !== lastDefaultsRaw) {
      lastDefaultsRaw = nextDefaultsRaw;
    }
    const nextValue = getFrontpageThreeDAndTerminalEnabled();
    if (nextValue === lastValue) return;
    lastValue = nextValue;
    onChange(nextValue);
  };

  const syncFromApi = (force = false) => {
    syncFrontpageThreeDAndTerminalEnabledFromApi(force)
      .catch(() => {
        // Ignore transient fetch failures and keep local fallback.
      })
      .finally(() => {
        notifyIfChanged();
      });
  };

  const handleStorage = (event) => {
    if (event.key && event.key !== GALLERY_DEFAULTS_STORAGE_KEY) return;
    notifyIfChanged();
  };

  const handleFocus = () => {
    notifyIfChanged();
    syncFromApi(true);
  };

  const handleVisibilityChange = () => {
    if (document.visibilityState !== 'visible') return;
    notifyIfChanged();
    syncFromApi(true);
  };

  const handleUpdateEvent = () => {
    notifyIfChanged();
  };

  notifyIfChanged();
  syncFromApi(true);

  const pollTimerId = window.setInterval(() => {
    notifyIfChanged();
    syncFromApi(false);
  }, 3000);

  window.addEventListener('storage', handleStorage);
  window.addEventListener('focus', handleFocus);
  document.addEventListener('visibilitychange', handleVisibilityChange);
  window.addEventListener(FRONTPAGE_TOGGLE_UPDATE_EVENT, handleUpdateEvent);

  return () => {
    window.clearInterval(pollTimerId);
    window.removeEventListener('storage', handleStorage);
    window.removeEventListener('focus', handleFocus);
    document.removeEventListener('visibilitychange', handleVisibilityChange);
    window.removeEventListener(FRONTPAGE_TOGGLE_UPDATE_EVENT, handleUpdateEvent);
  };
}

export function subscribeDefaulterTerminalFaqs(onChange) {
  if (!inBrowser()) return () => {};

  let lastDefaultsRaw = getGalleryDefaultsRaw();

  const notifyIfChanged = () => {
    const nextDefaultsRaw = getGalleryDefaultsRaw();
    if (nextDefaultsRaw === lastDefaultsRaw) return;
    lastDefaultsRaw = nextDefaultsRaw;
    onChange();
  };

  const handleStorage = (event) => {
    if (event.key && event.key !== GALLERY_DEFAULTS_STORAGE_KEY) return;
    notifyIfChanged();
  };

  const handleFocus = () => {
    notifyIfChanged();
  };

  const handleVisibilityChange = () => {
    if (document.visibilityState !== 'visible') return;
    notifyIfChanged();
  };

  const pollTimerId = window.setInterval(() => {
    notifyIfChanged();
  }, 1500);

  window.addEventListener('storage', handleStorage);
  window.addEventListener('focus', handleFocus);
  window.addEventListener('visibilitychange', handleVisibilityChange);

  return () => {
    window.clearInterval(pollTimerId);
    window.removeEventListener('storage', handleStorage);
    window.removeEventListener('focus', handleFocus);
    window.removeEventListener('visibilitychange', handleVisibilityChange);
  };
}

export function saveDebotConfigs(configs) {
  const normalized = Array.isArray(configs)
    ? configs.map((entry, index) => normalizeBot(entry, index))
    : [];
  const next = normalized.length ? normalized : DEFAULT_BOTS;

  if (inBrowser()) {
    try {
      window.localStorage.setItem(STORAGE_KEY, JSON.stringify(next));
    } catch {
      // Ignore storage quota and serialization errors.
    }
    emitUpdate();
  }

  return next;
}

export function subscribeDebotConfigs(onChange) {
  if (!inBrowser()) return () => {};

  const handleStorage = (event) => {
    if (event.key && event.key !== STORAGE_KEY) return;
    onChange();
  };

  const handleUpdate = () => {
    onChange();
  };

  window.addEventListener('storage', handleStorage);
  window.addEventListener(UPDATE_EVENT, handleUpdate);

  return () => {
    window.removeEventListener('storage', handleStorage);
    window.removeEventListener(UPDATE_EVENT, handleUpdate);
  };
}

export function createDebotDraft() {
  const stamp = `${Date.now().toString(36)}-${Math.round(Math.random() * 1e6).toString(36)}`;
  return {
    id: `bot-${stamp}`,
    name: 'New bot',
    description: 'Describe what this bot handles.',
    online: true,
    faqs: [
      {
        id: `faq-${stamp}-1`,
        question: 'Example question?',
        answer: 'Example answer.'
      }
    ]
  };
}
